// =====================================================================
// ProtocolFormDialog (EHR Protocolos / S63) — cria um pacote de doses.
// Vincula um produto do estoque (baixa por dose) + valor (gera orçamento/contas
// a receber no financeiro). v1 sem seletor de profissional (entra depois).
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Typography, CircularProgress, InputAdornment
} from "@material-ui/core";
import { toast } from "react-toastify";
import { createProtocol, protocolErrorMessage } from "../../services/protocolService";
import { listStockProducts } from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const ProtocolFormDialog = ({ open, patientId, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("");
  const [stockQtyPerDose, setStockQtyPerDose] = useState("1");
  const [totalDoses, setTotalDoses] = useState("6");
  const [price, setPrice] = useState("");
  const [installments, setInstallments] = useState("1");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setProductId(""); setStockQtyPerDose("1");
      setTotalDoses("6"); setPrice(""); setInstallments("1");
      (async () => {
        try {
          const data = await listStockProducts({ pageSize: 200 });
          setProducts(Array.isArray(data?.products) ? data.products : []);
        } catch (e) { setProducts([]); }
      })();
    }
  }, [open]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Informe o nome do protocolo."); return; }
    const doses = parseInt(totalDoses, 10) || 0;
    if (!(doses >= 1 && doses <= 120)) { toast.error("Número de doses inválido (1 a 120)."); return; }
    setSaving(true);
    try {
      await createProtocol(patientId, {
        name: name.trim(),
        productId: productId || undefined,
        stockQtyPerDose: Number(String(stockQtyPerDose).replace(",", ".")) || 1,
        totalDoses: doses,
        price: Number(String(price).replace(",", ".")) || 0,
        installments: Number(installments) || 1
      });
      toast.success("Protocolo criado.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(protocolErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Novo protocolo</DialogTitle>
      <DialogContent>
        <TextField label="Nome do protocolo" value={name} onChange={(e) => setName(e.target.value)}
          fullWidth margin="dense" autoFocus placeholder="ex.: Emagrecimento, Reposição hormonal" />
        <TextField select label="Produto do estoque (baixa por dose)" value={productId}
          onChange={(e) => setProductId(e.target.value)} fullWidth margin="dense"
          helperText="Opcional. Cada dose aplicada desconta do estoque deste produto.">
          <MenuItem value=""><em>Sem vínculo de estoque</em></MenuItem>
          {products.map((p) => (
            <MenuItem key={p.id} value={String(p.id)}>
              {p.name}{p.currentQty != null ? ` — saldo ${Number(p.currentQty)} ${p.unit || ""}` : ""}
            </MenuItem>
          ))}
        </TextField>
        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
          <TextField label="Nº de doses" type="number" value={totalDoses}
            onChange={(e) => setTotalDoses(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }}
            inputProps={{ min: 1, max: 120 }} />
          <TextField label="Baixa de estoque por dose" type="number" value={stockQtyPerDose}
            onChange={(e) => setStockQtyPerDose(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }}
            inputProps={{ step: "0.01", min: "0" }} disabled={!productId} />
        </Box>
        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
          <TextField label="Valor do pacote" type="number" value={price}
            onChange={(e) => setPrice(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }}
            inputProps={{ step: "0.01", min: "0" }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
          <TextField label="Parcelas" type="number" value={installments}
            onChange={(e) => setInstallments(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }}
            inputProps={{ min: 1, max: 60 }} disabled={!price} />
        </Box>
        <Typography variant="caption" color="textSecondary">
          Com valor preenchido, o pacote gera um orçamento aprovado → contas a receber no Financeiro.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Criar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProtocolFormDialog;
